import { isNaN as _isNaN, isNil as _isNil } from 'lodash-es';
import {
  listDeals,
  getDeal,
  getSimilarProperties,
  signBrokerOrder,
  createEvaluation,
} from '@/services/apiMapper';
import { cacheImages } from '@/services/cache';
import { apiToStateDeal } from '@/services/deal';
import { getSimilarPropertiesWithScreenshots } from '@/services/similarProperty';
import {
  getFailedRequests,
  FAILED_REQUEST_TYPE,
  saveFailedRequest,
  removeFailedRequest,
  compareFailedRequests,
} from '@/services/failedRequest';
import { BROKER_ORDER_ERROR_REASON } from '@/config/CONSTANTS';
import { FILTERS } from '@/config/DEAL';
import { ACTION as STORE_ACTION } from '@/store/CONSTANTS';
import { ACTION, MUTATION } from './CONSTANTS';

export default {
  async [ACTION.FETCH_DEALS]({ commit }, { filter = FILTERS.FUTURE } = {}) {
    const apiDeals = await listDeals(filter);
    const deals = apiDeals.map((apiDeal) => {
      const stateDeal = apiToStateDeal(apiDeal, { withComputed: true });
      const failedRequests = getFailedRequests({ dealUid: stateDeal.uid });

      return {
        ...stateDeal,
        filter,
        failedRequests,
      };
    });

    commit(MUTATION.ADD_DEALS, deals);

    if (filter === FILTERS.FUTURE) {
      cacheImages(deals.map((deal) => deal.mapImage));

      deals.forEach(async (deal) => {
        const similarProperties = await getSimilarProperties(deal);

        // We cache the photos of the similar properties
        getSimilarPropertiesWithScreenshots(similarProperties).forEach((property) => {
          cacheImages(property.photos);
        });
      });
    }

    return deals;
  },
  async [ACTION.FETCH_SINGLE_DEAL]({ dispatch, commit }, dealUid) {
    const stateDeal = await dispatch(ACTION.FETCH_DEAL, dealUid);
    const failedRequests = getFailedRequests({ dealUid });
    const deal = {
      ...stateDeal,
      failedRequests,
    };

    commit(MUTATION.SET_DEALS, [deal]);

    return deal;
  },
  async [ACTION.FETCH_DEAL](_, dealUid) {
    const deal = await getDeal(dealUid);

    return apiToStateDeal(deal, { withComputed: true });
  },
  async [ACTION.SIGN_BROKER_ORDER]({ dispatch, commit }, { dealUid, brokerOrderParams }) {
    try {
      await signBrokerOrder(brokerOrderParams);

      // We set the last evaluation to public=true, so we can alwasy send the right value
      commit(MUTATION.UPDATE_DEAL_PROPERTIES, {
        uid: dealUid,
        properties: {
          lastEvaluation: {
            public: true,
          },
        },
        deep: true,
      });

      dispatch(ACTION.REMOVE_FAILED_REQUEST, {
        dealUid,
        type: FAILED_REQUEST_TYPE.BROKER_ORDER,
      });
    } catch (error) {
      // backend returns the errors in various shapes so we have to find the exact reason why
      // it failed so we can show appropriate messages on the overview and instruction slide
      let reason = BROKER_ORDER_ERROR_REASON.UNKNOWN;
      let errorMessage;
      if (error.message === 'Network Error') {
        reason = BROKER_ORDER_ERROR_REASON.OFFLINE;
      } else if (error?.response.status === 422) {
        const responseData = error.response.data;
        if (responseData?.error?.length) {
          reason = BROKER_ORDER_ERROR_REASON.ERROR_MESSAGE;
          errorMessage = responseData.error;
        }
        // eslint-disable-next-line camelcase
        if (responseData?.phone_number) reason = BROKER_ORDER_ERROR_REASON.PHONE;
        if (responseData?.error && responseData.error.includes('hard-bounce'))
          reason = BROKER_ORDER_ERROR_REASON.HARD_BOUNCE;
        if (responseData?.error && responseData.error.includes('soft-bounce'))
          reason = BROKER_ORDER_ERROR_REASON.SOFT_BOUNCE;
      } else if (error?.response.status === 429) {
        reason = BROKER_ORDER_ERROR_REASON.TOO_MANY_REQUESTS;
      }

      dispatch(ACTION.ADD_FAILED_REQUEST, {
        dealUid,
        type: FAILED_REQUEST_TYPE.BROKER_ORDER,
        actionType: STORE_ACTION.WIZARD.SIGN_BROKER_ORDER,
        actionParams: { dealUid, brokerOrderParams, errorMessage },
        reason,
      });
      return Promise.reject(error);
    }
    return Promise.resolve();
  },
  async [ACTION.CREATE_EVALUATION]({ commit, dispatch }, { dealUid, evaluation }) {
    try {
      // TODO: Remove this temporary solution at any time after 15/09/2020
      // Temporary solution START
      // eslint-disable-next-line no-param-reassign
      evaluation.price_contributions = evaluation.price_contributions.filter(
        ({ value }) => !_isNil(value) && !_isNaN(value),
      );
      // Temporary solution END

      await createEvaluation(evaluation);
      const updatedStateDeal = await dispatch(ACTION.FETCH_DEAL, dealUid);

      // We call the update because the new deal won't have future/past property
      commit('updateDealProperties', {
        uid: dealUid,
        properties: {
          ...updatedStateDeal,
          unsavedEvaluation: null,
        },
      });

      dispatch(ACTION.REMOVE_FAILED_REQUEST, {
        dealUid,
        type: FAILED_REQUEST_TYPE.EVALUATION,
      });
    } catch {
      // We save the failedRequest to allow the user to try sending it later
      dispatch(ACTION.ADD_FAILED_REQUEST, {
        dealUid,
        type: FAILED_REQUEST_TYPE.EVALUATION,
        actionType: STORE_ACTION.WIZARD.CREATE_EVALUATION,
        actionParams: { dealUid, evaluation },
      });
    }
  },
  [ACTION.ADD_FAILED_REQUEST]({ commit, state }, failedRequest) {
    const { failedRequests = [] } = state.deals.find(({ uid }) => uid === failedRequest.dealUid);

    // We filter the failedRequest of the same type, as we want only one per type
    // we also sort the array to show the notifications in the order we want
    const updatedFailedRequests = [
      ...failedRequests.filter(({ type }) => type !== failedRequest.type),
      failedRequest,
    ].sort(compareFailedRequests);

    commit('updateDealProperties', {
      uid: failedRequest.dealUid,
      properties: {
        failedRequests: updatedFailedRequests,
      },
    });

    saveFailedRequest(failedRequest);
  },
  [ACTION.REMOVE_FAILED_REQUEST]({ commit, state }, { dealUid, type }) {
    const { failedRequests = [] } = state.deals.find(({ uid }) => uid === dealUid);

    commit('updateDealProperties', {
      uid: dealUid,
      properties: {
        failedRequests: failedRequests.filter(({ type: requestType }) => requestType !== type),
      },
    });

    removeFailedRequest({ dealUid, type });
  },
};
